/**
 * Aufgaben-Priorität wie in CRM 1.0: 0 = Normal, 1 = Hoch.
 * Aufgaben mit Priorität „Hoch" werden in Alarm-Rot dargestellt.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../../lib/theme';

export const TASK_PRIORITY_NORMAL = 0;
export const TASK_PRIORITY_HIGH = 1;

/** Alarm-Rot für Priorität „Hoch". */
export const ALARM_RED = '#d92d20';

export function isHighPriority(task: { priority?: unknown; status?: unknown } | null | undefined): boolean {
  return Number(task?.priority ?? 0) >= TASK_PRIORITY_HIGH;
}

/**
 * Zeilen-Stil für Aufgaben mit Priorität „Hoch" (erledigte bleiben neutral).
 * Über `...highPriorityRowStyle(task, dark)` in den Zeilen-Stil mischen.
 */
export function highPriorityRowStyle(
  task: { priority?: unknown; status?: unknown },
  dark: boolean,
): React.CSSProperties {
  if (!isHighPriority(task) || task.status === 1) return {};
  return {
    background: dark ? '#3a0f0c' : '#fef3f2',
    boxShadow: `inset 4px 0 0 ${ALARM_RED}`,
  };
}

/** Rotes Kennzeichen „Hoch" für Aufgaben-Zeilen. */
export function TaskPriorityBadge({ task }: { task: { priority?: unknown; status?: unknown } }) {
  const { t } = useTranslation();
  if (!isHighPriority(task)) return null;
  return (
    <span
      data-testid="task-priority-high"
      title={t('pflege.tasks.priority.high')}
      style={{
        fontSize: 10, fontWeight: 800, borderRadius: 999, padding: '1px 7px', flexShrink: 0,
        background: ALARM_RED, color: '#fff', whiteSpace: 'nowrap', textTransform: 'uppercase',
        letterSpacing: '0.04em', opacity: task.status === 1 ? 0.6 : 1,
      }}
    >
      {t('pflege.tasks.priority.badge')}
    </span>
  );
}

/** Knopf „Priorität hoch" — schaltet zwischen Normal (0) und Hoch (1) um. */
export function TaskPriorityToggle({ dark, value, onChange, compact = false }: {
  dark: boolean;
  value: number;
  onChange: (priority: number) => void;
  compact?: boolean;
}) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();
  const high = value >= TASK_PRIORITY_HIGH;
  return (
    <button
      type="button"
      data-testid="task-priority-toggle"
      aria-pressed={high}
      onClick={(e) => { e.stopPropagation(); onChange(high ? TASK_PRIORITY_NORMAL : TASK_PRIORITY_HIGH); }}
      style={{
        alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: compact ? 12 : 13, fontWeight: 700, padding: compact ? '5px 10px' : '8px 12px',
        borderRadius: 8, cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
        border: `1px solid ${high ? ALARM_RED : tc.border}`,
        background: high ? ALARM_RED : tc.inputBg,
        color: high ? '#fff' : ALARM_RED,
      }}
    >
      <span aria-hidden="true">!</span>
      {t('pflege.tasks.priority.high')}
    </button>
  );
}
